import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext.jsx';
import { fetchResearchTool, updateResearchTool } from '../lib/researchTools.js';
import ResearchToolForm from '../components/researchTools/ResearchToolForm.jsx';

export default function ResearchToolsEdit() {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAdmin, isActive } = useAuth();
  const [item, setItem] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // `t` deliberately excluded — see ScholarsEdit for the rationale.
  useEffect(() => {
    let cancelled = false;
    fetchResearchTool(id)
      .then((row) => !cancelled && (row ? setItem(row) : setError(t('researchTools.form.notFound'))))
      .catch((err) => !cancelled && setError(err.message ?? String(err)));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!isActive) {
    return (
      <div className="mx-auto max-w-2xl px-8 pb-20 pt-32">
        <div className="cta-box">
          <h1 className="cta-title">{t('bibliography.form.notActiveTitle')}</h1>
          <p className="cta-body">{t('bibliography.form.notActiveBody')}</p>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="mx-auto max-w-2xl px-8 pb-20 pt-32 text-center">
        <h1 className="font-serif text-3xl text-gold-light">{t('common.error')}</h1>
        <p className="mt-3 text-ink-secondary">{error}</p>
      </div>
    );
  }
  if (!item) {
    return (
      <div role="status" aria-live="polite" className="mx-auto max-w-3xl px-8 pb-20 pt-32 text-center text-ink-secondary">
        {t('common.loading')}
      </div>
    );
  }
  if (!isAdmin) {
    return (
      <div className="mx-auto max-w-2xl px-8 pb-20 pt-32 text-center">
        <h1 className="font-serif text-3xl text-gold-light">{t('common.error')}</h1>
        <p className="mt-3 text-ink-secondary">{t('researchTools.form.adminOnly')}</p>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="mx-auto max-w-2xl px-8 pb-20 pt-32">
        <div className="cta-box">
          <div className="section-label">{t('researchTools.form.savedLabel')}</div>
          <h1 className="cta-title">{item.name}</h1>
          <p className="cta-body">{t('researchTools.form.savedBody')}</p>
          <button type="button" className="btn-primary" onClick={() => navigate('/research-tools')}>
            {t('researchTools.form.backToList')}
          </button>
        </div>
      </div>
    );
  }

  async function handleSubmit(form) {
    setBusy(true);
    try {
      await updateResearchTool(item.id, form);
      setSubmitted(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-3xl px-6 pb-20 pt-28">
      <header className="mb-8">
        <div className="section-label">{t('researchTools.form.editLabel')}</div>
        <h1 className="font-serif text-3xl font-semibold text-cream">{item.name}</h1>
      </header>

      <ResearchToolForm
        initialValue={item}
        submitLabel={t('researchTools.form.save')}
        onSubmit={handleSubmit}
        onCancel={() => navigate('/research-tools')}
        busy={busy}
      />
    </div>
  );
}
